import React, { useContext } from "react";
import styled, { ThemeContext, StyledComponent } from "styled-components";
import { propStyle, themeStyle } from "utils/styles";

const buttonColors = {
  light: "#fff",
  dark: "#353535"
};

const buttonBackground = propStyle("color", buttonColors, {
  defaultKey: "light"
});

const buttonSizes = {
  small: "36px",
  normal: "44px"
};

const buttonHeight = propStyle("size", buttonSizes, { defaultKey: "normal" });

const buttonBorderColors = {
  light: "#dcdcdc",
  dark: "#5d5d5d"
};

const buttonBorderColor = propStyle("color", buttonBorderColors, {
  defaultKey: "light"
});

const buttonIconColors: { [key: string]: string } = {
  light: "#111",
  dark: "#fff"
};

const buttonIconStyles = ({
  iconPosition = "start",
  size = "normal",
  hasSibling = false
}) => {
  if (!hasSibling) return {};
  if (size === "small") {
    return { margin: iconPosition === "end" ? "0 0 0 4px" : "0 4px 0 0" };
  } else {
    return { margin: iconPosition === "end" ? "0 0 0 8px" : "0 8px 0 0" };
  }
};

const iconPositions = {
  start: "row",
  end: "row-reverse"
};

const buttonIconPosition = propStyle("iconPosition", iconPositions, {
  defaultKey: "start"
});

const buttonIconSizes: { [key: string]: number } = {
  small: 16,
  normal: 24
};

interface StyledButtonProps {
  size: string | number;
  color: string;
  iconPosition?: string;
}

const BaseButton = styled.button<StyledButtonProps>`
  height: ${buttonHeight};
  background: ${buttonBackground};
  border: 1px solid ${buttonBorderColor};
  border-radius: 4px;
  box-shadow: ${themeStyle("shadows.raised1")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: all 0.2s ease;

  &:focus,
  &:hover {
    box-shadow: none;
  }

  &:focus {
    border: 1px solid ${themeStyle("colors.primary")};

    svg {
      fill: ${themeStyle("colors.primary")};
    }
  }
`;

const StandardButton = styled(BaseButton)`
  padding: 0 40px;
`;

const EdgeLeftButton = styled(BaseButton)`
  padding: 0 20px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

const EdgeRightButton = styled(BaseButton)`
  padding: 0 20px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;

const CircleButton = styled(BaseButton)`
  width: ${buttonHeight};
  border-radius: 100%;
`;

const IconButton = styled(StandardButton)`
  padding: 0 20px;
  flex-direction: ${buttonIconPosition};
`;

type StyledButton = StyledComponent<"button", any, StyledButtonProps, never>;

const buttonTypes: {
  [key: string]: React.FC<StyledButtonProps>;
} = {
  standard: StandardButton,
  "edge-left": EdgeLeftButton,
  "edge-right": EdgeRightButton,
  circle: CircleButton,
  icon: IconButton
};

interface ButtonProps {
  color?: string;
  type?: string;
  size?: string;
  iconPosition?: string;
  renderIcon?: (args: {
    size: number;
    color: string;
    style: object;
  }) => React.ReactNode;
}

const Button: React.FC<ButtonProps & React.HTMLProps<HTMLButtonElement>> = ({
  color,
  type = "standard",
  size = "normal",
  renderIcon,
  iconPosition,
  children,
  ...props
}) => {
  const { darkMode } = useContext(ThemeContext);
  const ButtonComponent = buttonTypes[type];
  if (!color) color = darkMode ? "dark" : "light";
  return (
    <ButtonComponent
      size={size}
      color={color}
      iconPosition={iconPosition}
      {...props}
    >
      {renderIcon &&
        renderIcon({
          size: buttonIconSizes[size],
          color: buttonIconColors[color],
          style: buttonIconStyles({
            iconPosition,
            size,
            hasSibling: React.Children.count(children) > 0
          })
        })}
      {children}
    </ButtonComponent>
  );
};

export default Button;
